import React, { useEffect, useState } from "react";
import { supabase } from "../../supabaseClient";
import { Link } from "react-router-dom";
import CreateSquad from "../createSquad/CreateSquad";
import { useTournamentAuth } from "../../utils/authorization";
import { useDeleteAnimation } from "../../hooks/useDeleteAnimation";
import DeleteAnimationWrapper from "../UI/DeleteAnimationWrapper";
import { squadTimeLabel } from "../../utility/createSquad";
import { deleteEventInstancesForSquad } from "../../utility/squadEventInstances";
import {
  formatSquadListDate,
  groupSquadsByDateAndHouse,
} from "../../utility/squadSchedule";
import { SkeletonRows } from "../UI/DesignStandards";
import { scoringHubSquadPath } from "../../utility/squadSchedule";
import { useTournamentSlug } from "../../context/TournamentRouteContext";
import {
  decorateSquadEventNames,
  squadEventLabel,
} from "../../utility/squadEventNames";

const SquadEventRow = ({
  squad,
  tournamentID,
  canManage,
  confirmDeleteId,
  setConfirmDeleteId,
  setEditingSquad,
  handleDeleteSquad,
}) => {
  const tournamentSlug = useTournamentSlug();
  const eventName = squadEventLabel(squad);
  const sideEvents = (squad.squadEvents || [])
    .map((event) => event.event_name)
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 py-2">
      <div className="min-w-0">
        <span className="font-display font-bold text-brand-charcoal">
          {eventName}
        </span>
        {sideEvents ? (
          <span className="block text-sm text-brand-muted">{sideEvents}</span>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-2 shrink-0">
        <span className="text-sm text-brand-muted">
          {squad.teamCount} {squad.teamCount === 1 ? "team" : "teams"}
        </span>
        <Link
          to={scoringHubSquadPath(tournamentSlug || tournamentID, squad.id)}
          className="inline-flex items-center min-h-11 px-2 text-sm font-display font-bold tracking-wide text-brand-charcoal hover:text-brand-gold-ink"
        >
          Teams
        </Link>
        {canManage ? (
          <button
            type="button"
            onClick={() => setEditingSquad(squad)}
            className="inline-flex items-center min-h-11 px-2 text-sm font-display font-bold tracking-wide text-brand-muted hover:text-brand-charcoal"
          >
            Edit
          </button>
        ) : null}
        {canManage && squad.teamCount === 0 ? (
          confirmDeleteId === squad.id ? (
            <span className="inline-flex items-center gap-2 text-sm">
              <span className="text-brand-muted">Delete?</span>
              <button
                type="button"
                onClick={() => handleDeleteSquad(squad.id)}
                className="inline-flex items-center min-h-11 px-2 font-display font-bold tracking-wide text-red-600 hover:text-red-700"
              >
                Yes
              </button>
              <button
                type="button"
                onClick={() => setConfirmDeleteId(null)}
                className="inline-flex items-center min-h-11 px-2 font-display font-bold tracking-wide text-brand-muted hover:text-brand-charcoal"
              >
                No
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDeleteId(squad.id)}
              className="inline-flex items-center min-h-11 px-2 text-sm font-display font-bold tracking-wide text-brand-muted hover:text-red-600"
            >
              Delete
            </button>
          )
        ) : null}
      </div>
    </div>
  );
};

const SquadList = ({ tournamentID, embedded = false, onSquadsChange }) => {
  const [squads, setSquads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [editingSquad, setEditingSquad] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const { canManage } = useTournamentAuth(tournamentID);
  const {
    startDeleteAnimation,
    completeDeleteAnimation,
    cancelDeleteAnimation,
    isDeleting,
  } = useDeleteAnimation();

  const fetchSquads = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("squads")
      .select(
        `
        *,
        teams (
          id,
          isDeleted
        ),
        bowlingCenters (
          id,
          centerName,
          googlePlaceID,
          formattedAddress,
          phone,
          website
        )
      `
      )
      .eq("tournamentID", tournamentID)
      .is("isDeleted", false)
      .order("squadDate", { ascending: true })
      .order("squadTime", { ascending: true });

    if (error) {
      setErrorMessage("Error fetching squads. Please try again.");
      console.error("Error fetching squads:", error);
    } else {
      const processedData = await Promise.all(
        (data || []).map(async (squad) => {
          const { data: instancesData } = await supabase.rpc(
            "get_squad_event_instances",
            { p_squad_id: squad.id }
          );

          return {
            ...squad,
            teamCount:
              squad.teams?.filter((team) => !team.isDeleted).length || 0,
            squadEvents: instancesData || [],
          };
        })
      );
      setSquads(await decorateSquadEventNames(processedData));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSquads();
  }, [tournamentID]);

  const handleDeleteSquad = async (squadId) => {
    try {
      startDeleteAnimation(squadId);
      await deleteEventInstancesForSquad(squadId);
      const { error } = await supabase
        .from("squads")
        .update({
          isDeleted: true,
          deletedAt: new Date().toISOString(),
        })
        .eq("id", squadId);

      if (error) throw error;

      completeDeleteAnimation(squadId, () => {
        setSquads((current) => current.filter((squad) => squad.id !== squadId));
      });
      onSquadsChange?.();
    } catch (error) {
      console.error("Error deleting squad:", error);
      setErrorMessage("Error deleting squad. Please try again.");
      cancelDeleteAnimation(squadId);
    }
  };

  const handleSquadUpdated = () => {
    setEditingSquad(null);
    fetchSquads();
    onSquadsChange?.();
  };

  const groups = groupSquadsByDateAndHouse(squads);

  if (loading) {
    return (
      <div className={embedded ? undefined : "card-board p-6"}>
        <SkeletonRows count={5} />
      </div>
    );
  }
  if (errorMessage) return <p className="text-red-600">{errorMessage}</p>;

  return (
    <div className={embedded ? undefined : "card-board p-6"}>
      {editingSquad && (
        <div className="mb-6">
          <CreateSquad
            tournamentID={tournamentID}
            onSquadCreated={handleSquadUpdated}
            onCancel={() => setEditingSquad(null)}
            editMode={true}
            squadToEdit={editingSquad}
            nested={embedded}
          />
        </div>
      )}

      {groups.length === 0 ? (
        <p className="text-brand-muted">No squads found for this tournament.</p>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <section key={group.key} className="card-inset p-4 lg:p-6">
              <header className="mb-3">
                <h3 className="font-display font-bold text-lg text-brand-charcoal">
                  {formatSquadListDate(group.date)}
                </h3>
                {group.houseName ? (
                  <p className="text-sm text-brand-muted">{group.houseName}</p>
                ) : null}
              </header>
              <div className="space-y-1">
                {group.sessions.map((session) => (
                  <div key={session.time} className="flex gap-4">
                    <div className="w-[4.75rem] shrink-0 font-score text-brand-charcoal pt-2">
                      {squadTimeLabel(session.time)}
                    </div>
                    <div className="flex-1 min-w-0 divide-y divide-brand-charcoal/10">
                      {session.squads.map((squad) => (
                        <DeleteAnimationWrapper
                          key={squad.id}
                          itemId={squad.id}
                          isDeleting={isDeleting(squad.id)}
                        >
                          <SquadEventRow
                            squad={squad}
                            tournamentID={tournamentID}
                            canManage={canManage}
                            confirmDeleteId={confirmDeleteId}
                            setConfirmDeleteId={setConfirmDeleteId}
                            setEditingSquad={setEditingSquad}
                            handleDeleteSquad={handleDeleteSquad}
                          />
                        </DeleteAnimationWrapper>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default SquadList;
